/** @jsxImportSource @emotion/react */
import { useRef, useState } from "react";
import { Tag } from "primereact/tag";
import { Divider } from "primereact/divider";
import { Toast } from "primereact/toast";
import { Boton, Titulo, CampoInformacion, downloadFile } from "../../shared";

// Vista del Análisis de IA para el auditor. Muestra la conclusión (con botón
// para volcar al cierre de paso) y los productos propuestos (con botón para
// agregar cada uno al proceso). Al final, botón para marcar el análisis como
// procesado. Si ya está procesado o el paso es de solo lectura, oculta las
// acciones pero sigue mostrando el resultado.
export default function AnalisisIA({
  analisis,
  pasoId,
  proceso,
  modoLectura,
  onVolcarConclusion,
  onAgregarProducto,
  onMarcarProcesado,
  loaderProcesar
}) {
  const toast = useRef(null);
  const [descargandoId, setDescargandoId] = useState(null);
  const [loaderExcelProductos, setLoaderExcelProductos] = useState(false);

  // Descarga los productos propuestos en un Excel con el formato de la
  // plantilla de carga masiva
  function descargarExcelProductos() {
    setLoaderExcelProductos(true);
    // Arma el nombre del archivo con el cliente y el proceso, sin caracteres
    // que no puedan ir en un nombre de archivo
    const nombreArchivo = [
      "Productos Análisis IA",
      proceso?.cliente?.razonsocial,
      proceso?.nombre,
    ]
      .filter(Boolean)
      .join(" - ")
      .replace(/[\\/:*?"<>|]/g, "-");
    downloadFile(
      `colab/actividad/${pasoId}/analisis/productos/excel`,
      `${nombreArchivo}.xlsx`,
      () => setLoaderExcelProductos(false),
      (r) => {
        setLoaderExcelProductos(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: r?.[0]?.msg || "No se pudo descargar el archivo",
          life: 4000
        });
      }
    );
  }

  function descargar(doc) {
    setDescargandoId(doc.archivoId);
    downloadFile(
      `archivo/${doc.hash}/download`,
      doc.nombredescarga || `archivo-${doc.archivoId}`,
      () => setDescargandoId(null),
      (r) => {
        setDescargandoId(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: r?.[0]?.msg || "No se pudo descargar el archivo",
          life: 4000
        });
      }
    );
  }

  if (!analisis) {
    return (
      <>
        <Titulo>Análisis de IA</Titulo>
        <p css={{ color: "var(--grisTextos)" }}>
          No hay un análisis de IA para este proceso.
        </p>
      </>
    );
  }

  const procesado = analisis.procesado;
  const acciones = !procesado && !modoLectura;
  const documentos = Array.isArray(analisis.documentosGenerados)
    ? analisis.documentosGenerados
    : [];
  const conclusion = [analisis.decision, analisis.texto]
    .filter(Boolean)
    .join("\n\n");

  const tagEstado = (estado) => {
    const map = {
      volcado: { severity: "success", value: "Agregado" },
      descartado: { severity: "danger", value: "Descartado" },
      pendiente: { severity: "warning", value: "Pendiente" }
    };
    const t = map[estado] || { severity: "info", value: estado };
    return <Tag severity={t.severity} value={t.value} />;
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Titulo
        botones={procesado ? <Tag severity="success" value="Procesado" /> : undefined}
      >
        Análisis de IA
      </Titulo>

      {/* Conclusión */}
      <div css={{ marginBottom: "1em" }}>
        <div
          css={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0.5em"
          }}
        >
          <strong>Conclusión</strong>
          {acciones && conclusion && (
            <Boton
              transparente
              callback={() => onVolcarConclusion(conclusion)}
            >
              Volcar a cierre de paso
            </Boton>
          )}
        </div>
        {analisis.decision && (
          <div className="grid">
            <CampoInformacion nombreCampo="Decisión" anchoCompleto>
              <span css={{ whiteSpace: "pre-wrap" }}>{analisis.decision}</span>
            </CampoInformacion>
          </div>
        )}
        {analisis.texto && (
          <div className="grid">
            <CampoInformacion nombreCampo="Análisis" anchoCompleto>
              <span css={{ whiteSpace: "pre-wrap" }}>{analisis.texto}</span>
            </CampoInformacion>
          </div>
        )}
      </div>

      <Divider />

      {documentos.length > 0 && (
        <>
          <p css={{ color: "var(--grisTextos)", marginBottom: "0.5em" }}>
            Documentos generados
          </p>
          <div
            css={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.5em",
              marginBottom: "1em"
            }}
          >
            {documentos.map((doc) => (
              <Boton
                key={doc.archivoId}
                transparente
                loader={descargandoId === doc.archivoId}
                callback={() => descargar(doc)}
              >
                <i className="pi pi-download" /> {doc.nombredescarga}
              </Boton>
            ))}
          </div>
        </>
      )}

      <Titulo
        botones={
          (analisis.productosPropuestos?.length || 0) > 0 && (
            <Boton
              transparente
              loader={loaderExcelProductos}
              callback={descargarExcelProductos}
            >
              <i className="pi pi-download" /> Descargar Excel
            </Boton>
          )
        }
      >
        Productos propuestos ({analisis.productosPropuestos?.length || 0})
      </Titulo>

      {(analisis.productosPropuestos || []).map((producto, idx) => {
        const campos = [...(producto.campos || [])].sort(
          (a, b) =>
            (a.productocampospropios?.orden || 0) -
            (b.productocampospropios?.orden || 0)
        );
        const caracteristicas = [...(producto.caracteristicas || [])].sort(
          (a, b) =>
            (a.caracteristicacritica?.orden || 0) -
            (b.caracteristicacritica?.orden || 0)
        );
        const yaAgregado = producto.estado === "volcado";
        return (
          <div key={producto.id} css={{ marginBottom: "1em" }}>
            {idx > 0 && <Divider />}
            <div
              css={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "0.5em"
              }}
            >
              {tagEstado(producto.estado)}
              {acciones && !yaAgregado && (
                <Boton verde callback={() => onAgregarProducto(producto)}>
                  <i className="pi pi-plus" /> Agregar al proceso
                </Boton>
              )}
            </div>
            <div className="grid">
              {campos.map((campo) => (
                <CampoInformacion
                  key={campo.id}
                  nombreCampo={campo.productocampospropios?.etiqueta}
                >
                  {campo.valor
                    ? `${campo.valor}${
                        campo.productocampospropios?.unidades
                          ? " " + campo.productocampospropios.unidades
                          : ""
                      }`
                    : "-"}
                </CampoInformacion>
              ))}
            </div>

            {caracteristicas.length > 0 && (
              <>
                <Divider />
                <p css={{ color: "var(--grisTextos)", marginBottom: "0.5em" }}>
                  Características críticas
                </p>
                <div className="grid">
                  {caracteristicas.map((carac) => (
                    <CampoInformacion
                      key={carac.id}
                      nombreCampo={carac.caracteristicacritica?.etiqueta}
                    >
                      {carac.valor || "-"}
                    </CampoInformacion>
                  ))}
                </div>
              </>
            )}
          </div>
        );
      })}

      {acciones && (
        <div
          css={{
            marginTop: "1.5em",
            display: "flex",
            justifyContent: "flex-end"
          }}
        >
          <Boton
            verde
            loader={loaderProcesar}
            confirmacion="¿Marcar el análisis de IA como procesado?"
            callback={() => onMarcarProcesado(analisis.id)}
          >
            <i className="pi pi-check" /> Marcar procesado
          </Boton>
        </div>
      )}
    </>
  );
}
